import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card, Spin, Row, Col, Table, Tag, Empty, Space, DatePicker, Modal, Form, Input,
  InputNumber, Select, Button, Tabs, Upload, Statistic, message, Popconfirm, Tooltip,
} from 'antd';
import {
  AccountBookOutlined, FileTextOutlined, UploadOutlined, SettingOutlined,
  PlusOutlined, DeleteOutlined, EditOutlined, InboxOutlined, SyncOutlined, AuditOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { yuan } from '../utils/format.js';
import { apiFetch, onDataChanged } from '../utils/api.js';

const { Dragger } = Upload;

// 国内红涨绿跌：盈利=红，亏损=绿
function money(v) {
  const n = Number(v) || 0;
  const color = n > 0 ? '#CF1322' : n < 0 ? '#3F8600' : '#8C8178';
  return <span style={{ color, fontWeight: 600 }}>{n < 0 ? '-' : ''}¥{yuan(Math.abs(n))}</span>;
}

// 科目分类 → Tag 颜色
const CAT_COLOR = { '收入': 'red', '成本': 'volcano', '平台费用': 'orange', '经营费用': 'gold', '往来': 'default' };

export default function LedgerFlow() {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search || '').get('tab') === 'match' ? 'match' : 'report';
  const [meta, setMeta] = useState({ accounts: [], entities: [], stores: [] });
  const [report, setReport] = useState(null);
  const [flows, setFlows] = useState({ total: 0, data: [] });
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(dayjs()); // 报表/流水的月份过滤
  const [filters, setFilters] = useState({ entity_id: 0, store_id: 0, account_code: '', direction: '', kw: '' });

  // 录入弹框
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm] = Form.useForm();
  // 映射管理弹框
  const [entityOpen, setEntityOpen] = useState(false);
  const [entityEdit, setEntityEdit] = useState(null);
  const [entityForm] = Form.useForm();
  const [storeOpen, setStoreOpen] = useState(false);
  const [storeEdit, setStoreEdit] = useState(null);
  const [storeForm] = Form.useForm();

  const mStr = month ? month.format('YYYY-MM') : '';

  const loadMeta = () => apiFetch('/api/ledger/meta')
    .then(r => r.json())
    .then(d => { setMeta({ accounts: d.accounts || [], entities: d.entities || [], stores: d.stores || [] }); });

  const loadReport = () => {
    setLoading(true);
    const q = mStr ? `?month=${mStr}` : '';
    apiFetch('/api/ledger/report' + q)
      .then(r => r.json())
      .then(d => { setReport(d.report || null); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const loadFlows = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (mStr) p.set('month', mStr);
    if (filters.entity_id) p.set('entity_id', filters.entity_id);
    if (filters.store_id) p.set('store_id', filters.store_id);
    if (filters.account_code) p.set('account_code', filters.account_code);
    if (filters.direction) p.set('direction', filters.direction);
    if (filters.kw) p.set('kw', filters.kw);
    p.set('page_size', '500');
    apiFetch('/api/ledger/flow?' + p.toString())
      .then(r => r.json())
      .then(d => { setFlows({ total: d.total || 0, data: d.data || [] }); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadMeta();
  }, []);
  useEffect(() => { loadReport(); }, [month]);
  useEffect(() => { loadFlows(); }, [month, filters]);
  useEffect(() => onDataChanged(() => { loadReport(); loadFlows(); }), [month, filters]);

  const recalcCost = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/ledger/recalc-cost', { method: 'POST' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || '重算失败');
      message.success(`成本已重算：匹配 ${j.matchedOrders} 单${j.unmatchedOrders ? `，${j.unmatchedOrders} 单未匹配` : ''}`);
      loadReport(); loadFlows();
    } catch (e) { message.error(e.message || '重算失败'); setLoading(false); }
  };

  const entityName = (id) => (meta.entities.find(e => Number(e.id) === Number(id)) || {}).name || '';
  const storeName = (id) => (meta.stores.find(s => Number(s.id) === Number(id)) || {}).name || '';

  // 快捷录入提交
  const submitEntry = async () => {
    try {
      const v = await entryForm.validateFields();
      // DatePicker 值是 dayjs 对象，JSON.stringify 会变成 ISO 带时间格式（后端只认 YYYY-MM-DD），必须先格式化
      if (v.biz_date && v.biz_date.format) v.biz_date = v.biz_date.format('YYYY-MM-DD');
      const res = await apiFetch('/api/ledger/flow', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || '录入失败');
      message.success('已记一笔流水');
      setEntryOpen(false);
      entryForm.resetFields();
      loadFlows(); loadReport();
    } catch (e) { message.error(e.message || '录入失败'); }
  };

  const deleteFlow = async (id) => {
    const res = await apiFetch('/api/ledger/flow/' + id, { method: 'DELETE' });
    const j = await res.json();
    if (!res.ok) { message.error(j.error || '删除失败'); return; }
    message.success('已删除');
    loadFlows(); loadReport();
  };

  // 拼多多导入
  const importPdd = async (file, storeId, bizDate) => {
    const form = new FormData();
    form.append('file', file);
    form.append('store_id', storeId);
    if (bizDate) form.append('biz_date', bizDate.format ? bizDate.format('YYYY-MM-DD') : String(bizDate).slice(0, 10));
    try {
      const res = await apiFetch('/api/ledger/import-pdd', { method: 'POST', body: form });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || '导入失败');
      const unknown = Object.entries(j.unknownCodes || {}).map(([k, v]) => `${k}(${v}笔)`).join('、');
      const cost = j.costMatchedOrders != null ? `；已按订单号自动匹配商品成本/运费/辅材 ${j.costMatchedOrders} 单` : '';
      const base = `导入成功：新增 ${j.added} 笔${j.skipped ? `，跳过重复 ${j.skipped} 笔` : ''}${unknown ? `；未映射编码：${unknown}` : ''}${cost}`;
      if (j.costUnmatchedOrders != null && j.costUnmatchedOrders > 0) {
        message.warning(`${base}；⚠️ ${j.costUnmatchedOrders} 单到账订单未匹配到订单管理（成本未计入，净利虚高），请到「对账核对」补录`, 8);
      } else {
        message.success(base);
      }
      loadFlows(); loadReport();
    } catch (e) { message.error(e.message || '导入失败'); }
  };

  // 主体/店铺映射保存
  const submitEntity = async () => {
    try {
      const v = await entityForm.validateFields();
      const res = await apiFetch('/api/ledger/entity', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: entityEdit ? entityEdit.id : undefined, ...v }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || '保存失败');
      message.success('已保存主体');
      setEntityOpen(false); entityForm.resetFields(); setEntityEdit(null); loadMeta();
    } catch (e) { message.error(e.message || '保存失败'); }
  };
  const submitStore = async () => {
    try {
      const v = await storeForm.validateFields();
      const res = await apiFetch('/api/ledger/store', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: storeEdit ? storeEdit.id : undefined, ...v }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || '保存失败');
      message.success('已保存店铺映射');
      setStoreOpen(false); storeForm.resetFields(); setStoreEdit(null); loadMeta();
    } catch (e) { message.error(e.message || '保存失败'); }
  };
  const deleteStore = async (id) => {
    const res = await apiFetch('/api/ledger/store/' + id, { method: 'DELETE' });
    const j = await res.json();
    if (!res.ok) { message.error(j.error || '删除失败'); return; }
    message.success('已删除'); loadMeta();
  };

  // 流水表格列
  const flowCols = [
    { title: '日期', dataIndex: 'biz_date', width: 110, fixed: 'left', sorter: (a, b) => String(a.biz_date).localeCompare(String(b.biz_date)) },
    { title: '科目', dataIndex: 'account_code', width: 150, render: (v, r) => (<div><span style={{ fontWeight: 600 }}>{r.account_name}</span><div style={{ fontSize: 11, color: '#B3A79C' }}>{v} · {r.account_category}</div></div>) },
    { title: '方向', dataIndex: 'direction', width: 70, align: 'center', render: v => <Tag color={v === '收入' ? 'red' : 'green'}>{v}</Tag> },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (v, r) => r.direction === '收入' ? money(v) : <span style={{ color: '#5B5048' }}>¥{yuan(v)}</span> },
    { title: '店铺', dataIndex: 'store_name', width: 170, render: v => v || <span style={{ color: '#B3A79C' }}>—</span> },
    { title: '主体', dataIndex: 'entity_name', width: 100 },
    { title: '来源', dataIndex: 'source', width: 90, render: v => <Tag>{v}</Tag> },
    { title: '摘要', dataIndex: 'summary', ellipsis: true },
    { title: '关联单号', dataIndex: 'ref_no', width: 170, ellipsis: true, render: v => v || '—' },
    { title: '备注', dataIndex: 'remark', width: 140, ellipsis: true, render: v => v || '—' },
    {
      title: '操作', width: 60, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Popconfirm title="删除这笔流水？" onConfirm={() => deleteFlow(r.id)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  // 报表的费用结构表：以科目字典为底，全部标准科目都显示（含 0 流水的税费等，让人知道可录），
  // 再补上字典外但流水里出现的历史科目（2026-09-26 ACCIO 审查项3）
  const accountRows = useMemo(() => {
    if (!report || !report.byAccount) return [];
    const rows = (meta.accounts || []).map(a => {
      const v = report.byAccount[a.code] || {};
      return { code: a.code, name: a.name, category: a.category, inc: v.inc || 0, exp: v.exp || 0, cnt: v.cnt || 0 };
    });
    for (const [code, v] of Object.entries(report.byAccount)) {
      if (!rows.some(r => r.code === code)) rows.push({ code, name: code, category: '其他', inc: v.inc || 0, exp: v.exp || 0, cnt: v.cnt || 0 });
    }
    return rows.sort((a, b) => (b.inc + b.exp) - (a.inc + a.exp));
  }, [report, meta]);

  const entityRows = useMemo(() => {
    if (!report || !report.byEntity) return [];
    return Object.entries(report.byEntity).map(([eid, v]) => ({
      entity_id: eid,
      name: entityName(eid) || (eid === '' ? '（未归属）' : eid),
      income: v.income || 0, platformFee: v.platformFee || 0, cogs: v.cogs || 0, opex: v.opex || 0,
    }));
  }, [report, meta]);

  const reportTab = (
    <Spin spinning={loading}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card className="kpi-tap"><Statistic title="净入账" value={report ? report.netIncome : 0} precision={2} prefix="¥" valueStyle={{ color: 'var(--brand)' }} /></Card></Col>
        <Col xs={12} md={6}><Card className="kpi-tap"><Statistic title="毛利" value={report ? report.grossProfit : 0} precision={2} prefix="¥" valueStyle={{ color: (report && report.grossProfit) >= 0 ? '#CF1322' : '#3F8600' }} /></Card></Col>
        <Col xs={12} md={6}><Card className="kpi-tap"><Statistic title="净利" value={report ? report.netProfit : 0} precision={2} prefix="¥" valueStyle={{ color: (report && report.netProfit) >= 0 ? '#CF1322' : '#3F8600' }} /></Card></Col>
        <Col xs={12} md={6}><Card className="kpi-tap"><Statistic
          title={<span>现金流净额<Tooltip title="当月真金白银的净变动 = 现金流入合计（含往来收入）− 现金流出合计（含往来支出；不含商品成本 5001——它是损益不是现金流出，也不含店铺→银行卡转账）">
            <QuestionCircleOutlined style={{ fontSize: 12, color: '#B3A79C', marginLeft: 4 }} />
          </Tooltip></span>}
          value={report ? report.cashNet : 0} precision={2} prefix="¥" valueStyle={{ color: '#5B5048' }} /></Card></Col>
      </Row>
      {report && (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card title="费用 / 收入结构（按科目）" size="small" style={{ marginBottom: 16 }}>
              <Table size="small" rowKey="code" dataSource={accountRows} pagination={false}
                columns={[
                  { title: '科目', dataIndex: 'name', render: (v, r) => (<div><span style={{ fontWeight: 600 }}>{v}</span><div style={{ fontSize: 11, color: '#B3A79C' }}>{r.code}</div></div>) },
                  { title: '分类', dataIndex: 'category', width: 90, render: v => <Tag color={CAT_COLOR[v] || 'default'}>{v}</Tag> },
                  { title: '收入', dataIndex: 'inc', align: 'right', render: v => v ? <span style={{ color: '#CF1322' }}>¥{yuan(v)}</span> : '—' },
                  { title: '支出', dataIndex: 'exp', align: 'right', render: v => v ? <span style={{ color: '#3F8600' }}>¥{yuan(v)}</span> : '—' },
                  { title: '笔数', dataIndex: 'cnt', align: 'right', width: 70 },
                ]} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="分主体（报税口径）" size="small" style={{ marginBottom: 16 }}>
              <Table size="small" rowKey="entity_id" dataSource={entityRows} pagination={false}
                columns={[
                  { title: '主体', dataIndex: 'name' },
                  { title: '收入', dataIndex: 'income', align: 'right', render: v => `¥${yuan(v)}` },
                  { title: '平台费用', dataIndex: 'platformFee', align: 'right', render: v => `¥${yuan(v)}` },
                  { title: '商品成本', dataIndex: 'cogs', align: 'right', render: v => `¥${yuan(v)}` },
                  { title: '经营费用', dataIndex: 'opex', align: 'right', render: v => `¥${yuan(v)}` },
                ]} />
            </Card>
          </Col>
        </Row>
      )}
      {!report && <Card><Empty description="还没有流水数据，先在「流水明细」录入或「拼多多导入」导入账单" /></Card>}
    </Spin>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#26201C' }}>账务流水</div>
          <div style={{ color: '#8C8178', fontSize: 12, marginTop: 4 }}>
            单表流水账：店铺流水 + 公司开销进同一张表，自动出净入账 / 毛利 / 净利；分主体独立报税（「个人」仅为归集，不算报税主体）
          </div>
        </div>
        <Space>
          <Button icon={<SyncOutlined />} onClick={recalcCost} loading={loading}>重算成本</Button>
          <DatePicker picker="month" value={month} allowClear onChange={v => setMonth(v || null)} placeholder="全部月份" />
        </Space>
      </div>

      <Tabs
        defaultActiveKey={initialTab}
        items={[
          {
            key: 'report', label: (<span><AccountBookOutlined /> 报表看板</span>), children: reportTab,
          },
          {
            key: 'flow', label: (<span><FileTextOutlined /> 流水明细</span>),
            children: (
              <Card>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Select allowClear placeholder="主体" style={{ width: 120 }} value={filters.entity_id || undefined}
                    onChange={v => setFilters({ ...filters, entity_id: v || 0 })}
                    options={meta.entities.map(e => ({ value: Number(e.id), label: e.name }))} />
                  <Select allowClear placeholder="店铺" style={{ width: 190 }} value={filters.store_id || undefined}
                    onChange={v => setFilters({ ...filters, store_id: v || 0 })}
                    options={meta.stores.map(s => ({ value: Number(s.id), label: s.name }))} />
                  <Select allowClear placeholder="科目" style={{ width: 150 }} value={filters.account_code || undefined}
                    onChange={v => setFilters({ ...filters, account_code: v || '' })}
                    options={meta.accounts.map(a => ({ value: a.code, label: `${a.name}(${a.code})` }))} />
                  <Select allowClear placeholder="方向" style={{ width: 90 }} value={filters.direction || undefined}
                    onChange={v => setFilters({ ...filters, direction: v || '' })}
                    options={[{ value: '收入', label: '收入' }, { value: '支出', label: '支出' }]} />
                  <Input.Search allowClear placeholder="搜摘要/单号/备注" style={{ width: 200 }} onSearch={v => setFilters({ ...filters, kw: v })} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => { entryForm.resetFields(); setEntryOpen(true); }}>记一笔</Button>
                </Space>
                <Table rowKey="id" size="small" columns={flowCols} dataSource={flows.data}
                  scroll={{ x: 1280 }} loading={loading}
                  pagination={{ pageSize: 50, showTotal: t => `共 ${t} 笔流水` }}
                  locale={{ emptyText: <Empty description="暂无流水" /> }} />
              </Card>
            ),
          },
          {
            key: 'import', label: (<span><UploadOutlined /> 拼多多导入</span>),
            children: (
              <Card>
                <ImportPdd meta={meta} onImport={importPdd} />
              </Card>
            ),
          },
          {
            key: 'mapping', label: (<span><SettingOutlined /> 主体 / 店铺</span>),
            children: (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 700, color: '#26201C' }}>主体（公司 + 个体户，报税各自独立）</span>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => { entityForm.resetFields(); setEntityEdit(null); setEntityOpen(true); }}>新增主体</Button>
                </div>
                <Table rowKey="id" size="small" dataSource={meta.entities} pagination={false} style={{ marginBottom: 24 }}
                  columns={[
                    { title: '名称', dataIndex: 'name' },
                    { title: '类型', dataIndex: 'type', width: 110, render: v => v === 'company' ? <Tag color="red">公司</Tag> : <Tag>个体户</Tag> },
                    { title: '纳税识别号', dataIndex: 'tax_no', render: v => v || '—' },
                    { title: '备注', dataIndex: 'remark', render: v => v || '—' },
                    { title: '操作', width: 70, align: 'center', render: (_, r) => <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { entityForm.setFieldsValue(r); setEntityEdit(r); setEntityOpen(true); }} /> },
                  ]} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 700, color: '#26201C' }}>店铺 → 主体映射</span>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => { storeForm.resetFields(); setStoreEdit(null); setStoreOpen(true); }}>新增店铺</Button>
                </div>
                <Table rowKey="id" size="small" dataSource={meta.stores} pagination={false}
                  columns={[
                    { title: '平台', dataIndex: 'platform', width: 90, render: v => <Tag>{v}</Tag> },
                    { title: '店铺名', dataIndex: 'name' },
                    { title: '归属主体', dataIndex: 'entity_id', width: 130, render: v => entityName(v) },
                    { title: '登录账号', dataIndex: 'login_account', render: v => v || '—' },
                    { title: '操作', width: 100, align: 'center', render: (_, r) => (
                      <Space size={0}>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { storeForm.setFieldsValue(r); setStoreEdit(r); setStoreOpen(true); }} />
                        <Popconfirm title="删除该店铺映射？" onConfirm={() => deleteStore(r.id)}><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                      </Space>
                    ) },
                  ]} />
              </Card>
            ),
          },
          {
            key: 'match', label: (<span><AuditOutlined /> 对账核对</span>),
            children: <OrderMatchPanel month={month} stores={meta.stores} />,
          },
        ]}
      />

      {/* 快捷录入弹框 */}
      <Modal title="记一笔流水" open={entryOpen} onOk={submitEntry} onCancel={() => setEntryOpen(false)} okText="保存" cancelText="取消" destroyOnClose>
        <Form form={entryForm} layout="vertical" initialValues={{ biz_date: dayjs(), source: '其他', direction: '支出', entity_id: 1 }}>
          <Form.Item name="biz_date" label="发生日期" rules={[{ required: true, message: '请选择日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="entity_id" label="主体" rules={[{ required: true, message: '请选择主体' }]}>
                <Select options={meta.entities.map(e => ({ value: Number(e.id), label: e.name }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="store_id" label="店铺（公司开销留空）">
                <Select allowClear options={meta.stores.map(s => ({ value: Number(s.id), label: s.name }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="account_code" label="科目" rules={[{ required: true, message: '请选择科目' }]}>
                <Select showSearch optionFilterProp="label" options={meta.accounts.map(a => ({ value: a.code, label: `${a.name}（${a.code}）` }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="direction" label="方向" rules={[{ required: true, message: '请选择方向' }]}>
                <Select options={[{ value: '收入', label: '收入' }, { value: '支出', label: '支出' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="金额（元，正数）" rules={[{ required: true, message: '请填金额' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="source" label="来源">
                <Select options={['其他', '银行', '微信', '支付宝', '手工'].map(v => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="summary" label="摘要" rules={[{ required: true, message: '请填摘要' }]}>
            <Input placeholder="如：9月工资 / 采购付款 / 老板投入" />
          </Form.Item>
          <Form.Item name="ref_no" label="关联单号（可选）">
            <Input placeholder="订单号 / 流水号" />
          </Form.Item>
          <Form.Item name="remark" label="备注（可选）">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 主体编辑弹框 */}
      <Modal title={entityEdit ? '编辑主体' : '新增主体'} open={entityOpen} onOk={submitEntity} onCancel={() => setEntityOpen(false)} okText="保存" cancelText="取消" destroyOnClose>
        <Form form={entityForm} layout="vertical" initialValues={{ type: 'company' }}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请填名称' }]}><Input placeholder="公司 / 个体户1" /></Form.Item>
          <Form.Item name="type" label="类型"><Select options={[{ value: 'company', label: '公司' }, { value: 'individual', label: '个体户' }]} /></Form.Item>
          <Form.Item name="tax_no" label="纳税识别号"><Input /></Form.Item>
          <Form.Item name="remark" label="备注"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* 店铺映射编辑弹框 */}
      <Modal title={storeEdit ? '编辑店铺' : '新增店铺'} open={storeOpen} onOk={submitStore} onCancel={() => setStoreOpen(false)} okText="保存" cancelText="取消" destroyOnClose>
        <Form form={storeForm} layout="vertical" initialValues={{ platform: '拼多多', entity_id: 1 }}>
          <Form.Item name="platform" label="平台"><Select options={['拼多多', '淘宝', '抖音', '1688'].map(v => ({ value: v, label: v }))} /></Form.Item>
          <Form.Item name="name" label="店铺名" rules={[{ required: true, message: '请填店铺名' }]}><Input /></Form.Item>
          <Form.Item name="entity_id" label="归属主体" rules={[{ required: true, message: '请选择主体' }]}>
            <Select options={meta.entities.map(e => ({ value: Number(e.id), label: e.name }))} />
          </Form.Item>
          <Form.Item name="login_account" label="登录账号（可选）"><Input /></Form.Item>
          <Form.Item name="remark" label="备注"><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// 拼多多导入子组件
function ImportPdd({ meta, onImport }) {
  const [storeId, setStoreId] = useState(null);
  const [file, setFile] = useState(null);
  const [bizDate, setBizDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const doImport = async () => {
    if (!storeId) { message.warning('请先选择店铺'); return; }
    if (!file) { message.warning('请先选择 CSV 文件'); return; }
    setLoading(true);
    await onImport(file, storeId, bizDate ? bizDate.format('YYYY-MM-DD') : '');
    setLoading(false);
  };
  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <span style={{ color: '#5B5048' }}>店铺：</span>
        <Select placeholder="选择店铺" style={{ width: 220 }} value={storeId}
          onChange={v => setStoreId(v)}
          options={meta.stores.filter(s => s.platform === '拼多多').map(s => ({ value: Number(s.id), label: s.name }))} />
        <span style={{ color: '#5B5048' }}>账单日期：</span>
        <DatePicker value={bizDate} onChange={v => setBizDate(v)} />
      </Space>
      <Dragger
        accept=".csv"
        maxCount={1}
        beforeUpload={(f) => { setFile(f); return false; }}
        onRemove={() => setFile(null)}
        fileList={file ? [{ uid: '-1', name: file.name }] : []}
        style={{ padding: '20px 0' }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">点击或拖拽拼多多「账务明细」CSV 到这里</p>
        <p className="ant-upload-hint">字段：商户订单号,发生时间,收入金额,支出金额,账务类型,备注,业务描述；按编码映射到标准科目，逐笔写流水（自动去重）</p>
      </Dragger>
      <Button type="primary" icon={<UploadOutlined />} onClick={doImport} loading={loading} style={{ marginTop: 12 }}>开始导入</Button>
      <div style={{ marginTop: 12, color: '#8C8178', fontSize: 12 }}>
        提示：先在「主体 / 店铺」里把拼多多店铺登记好并选对归属主体，导入才会自动带出主体。
      </div>
    </div>
  );
}

// 对账核对：订单匹配明细 + 未匹配清单（只读，数据源 = 账务流水到账订单 vs 订单表）
function OrderMatchPanel({ month, stores }) {
  const [storeId, setStoreId] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyLoss, setOnlyLoss] = useState(false);

  const mStr = month ? month.format('YYYY-MM') : '';
  const storeName = (id) => (stores.find(s => Number(s.id) === Number(id)) || {}).name || ('店铺' + id);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (mStr) p.set('month', mStr);
    if (storeId) p.set('store_id', storeId);
    apiFetch('/api/ledger/order-match?' + p.toString())
      .then(r => r.json())
      .then(d => { if (d && d.success !== false) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [mStr, storeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => onDataChanged(() => load()), [load]);

  const matched = (data && data.matched) || [];
  // 未匹配 = 全历史待处理（后端 unmatched_all 不受月份限制），始终显示直到补录
  const unmatched = (data && data.unmatched_all) || [];
  // 未核对数（排除「已核对/合并发货」标记）——用于卡片/横幅的「需处理」提示
  const uncheckedCount = data ? (data.unmatched_all_unchecked != null ? data.unmatched_all_unchecked : data.unmatched_count) : 0;
  const uncheckedIncome = data ? (data.unmatched_all_unchecked_income != null ? data.unmatched_all_unchecked_income : data.unmatched_income) : 0;
  let mrows = matched;
  if (search) {
    const s = search.toLowerCase();
    mrows = mrows.filter(r => r.order_no.toLowerCase().includes(s) || (r.product_name || '').toLowerCase().includes(s) || (r.spec_code || '').toLowerCase().includes(s));
  }
  if (onlyLoss) mrows = mrows.filter(r => (r.order_profit || 0) < 0);

  const copyNo = async (no) => {
    try { await navigator.clipboard.writeText(no); message.success('已复制 ' + no); }
    catch { message.warning('复制失败，请手动复制'); }
  };

  // 标记/取消「已核对」（合并发货等已知正常单，从未匹配报警里排除，列表仍显示）
  const checkNo = async (no, checked) => {
    try {
      const r = await apiFetch('/api/ledger/order-match/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref_no: no, checked }),
      });
      const j = await r.json();
      if (j && j.success !== false) { message.success(checked ? '已标记为「已核对」，不再微信/首页提醒' : '已取消标记'); load(); }
      else message.error((j && j.error) || '操作失败');
    } catch { message.error('操作失败'); }
  };

  return (
    <Card>
      <Space wrap style={{ marginBottom: 12 }}>
        <span style={{ color: '#5B5048', fontSize: 13 }}>店铺</span>
        <Select allowClear placeholder="全部店铺" style={{ width: 190 }} value={storeId || undefined}
          onChange={v => setStoreId(v || 0)}
          options={stores.map(s => ({ value: Number(s.id), label: s.name }))} />
        <span style={{ color: '#8C8178', fontSize: 12 }}>按月 = 上方月份选择器；到账订单 = 账务流水里拼多多销售收入（4001）带订单号的流水</span>
      </Space>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="到账订单数" value={data ? data.income_order_count : 0} suffix="单" /></Card></Col>
        <Col xs={12} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="已匹配" value={data ? data.matched_count : 0} suffix="单" valueStyle={{ color: '#3F8600' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="售后退款单" value={data ? (data.refunded_count || 0) : 0} suffix="单" valueStyle={{ color: '#B8860B' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="未匹配(需补录)" value={uncheckedCount} suffix="单" valueStyle={{ color: '#CF1322' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="未匹配到账" value={uncheckedIncome} precision={2} prefix="¥" valueStyle={{ color: '#CF1322' }} /></Card></Col>
        <Col xs={24} md={4}><Card size="small" style={{ borderRadius: 12 }}><Statistic title="已匹配成本合计" value={data ? data.cost_total : 0} precision={2} prefix="¥" /></Card></Col>
      </Row>

      {uncheckedCount > 0 && (
        <div style={{ background: '#FFFBE6', border: '1px solid #FFE58F', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--brand)', lineHeight: 1.7 }}>
          ⚠️ 还有 {uncheckedCount} 单有净到账，但订单管理里找不到（净到账合计 {money(uncheckedIncome)}），成本未计入 → 请去「订单管理」补录，再回「报表看板」点「重算成本」。
          {data.refunded_count > 0 && <> 另有 {data.refunded_count} 单为售后全额退款（钱已原路退回），无需补录，见「售后退款单」页签。</>}
          {unmatched.length > uncheckedCount && <> 另有 {unmatched.length - uncheckedCount} 单已标记「已核对」（合并发货等正常单），不再计入上方提醒。</>}
        </div>
      )}

      <Tabs
        defaultActiveKey="unmatched"
        size="small"
        items={[
          {
            key: 'unmatched', label: <span>未匹配 <Tag color="error" style={{ marginLeft: 4 }}>{unmatched.length}</Tag></span>,
            children: (
              <Table rowKey="order_no" size="small" dataSource={unmatched} loading={loading}
                pagination={{ pageSize: 50, showTotal: t => `共 ${t} 单未匹配` }}
                columns={[
                  { title: '订单号', dataIndex: 'order_no', width: 230, render: (v, r) => (
                    <span>
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
                      {r.checked && <Tag color="green" style={{ marginLeft: 6, fontSize: 11 }}>已核对</Tag>}
                    </span>
                  ) },
                  { title: '到账金额', dataIndex: 'income', width: 120, align: 'right', render: v => money(v) },
                  { title: '已退款', dataIndex: 'refund', width: 110, align: 'right', render: v => (v > 0 ? <span style={{ color: '#B8860B' }}>{money(v)}</span> : '—') },
                  { title: '店铺', dataIndex: 'store_id', width: 170, render: v => storeName(v) },
                  { title: '发生日期', dataIndex: 'biz_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                  { title: '操作', width: 180, render: (_, r) => (
                    <Space size={0}>
                      <Button size="small" type="link" onClick={() => copyNo(r.order_no)}>复制单号</Button>
                      {r.checked
                        ? <Button size="small" type="link" onClick={() => checkNo(r.order_no, false)}>取消标记</Button>
                        : <Button size="small" type="link" style={{ color: '#3F8600' }} onClick={() => checkNo(r.order_no, true)}>标记已核对</Button>}
                    </Space>
                  ) },
                ]}
                locale={{ emptyText: <Empty description="全部到账订单都已匹配到订单管理" /> }} />
            ),
          },
          {
            key: 'refunded', label: <span>售后退款单 <Tag color="warning" style={{ marginLeft: 4 }}>{((data && data.refunded) || []).length}</Tag></span>,
            children: (
              <div>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#8C8178' }}>这些订单到账后又全额退款（未发货退款/退款成功等），钱已原路退回，无需补录订单；到账与退款在损益里互相抵消，不影响利润。</div>
                <Table rowKey="order_no" size="small" dataSource={(data && data.refunded) || []} loading={loading}
                  pagination={{ pageSize: 50, showTotal: t => `共 ${t} 单售后退款` }}
                  columns={[
                    { title: '订单号', dataIndex: 'order_no', width: 200, render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
                    { title: '到账金额', dataIndex: 'income', width: 120, align: 'right', render: v => money(v) },
                    { title: '退款金额', dataIndex: 'refund', width: 120, align: 'right', render: v => money(v) },
                    { title: '店铺', dataIndex: 'store_id', width: 170, render: v => storeName(v) },
                    { title: '发生日期', dataIndex: 'biz_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                  ]}
                  locale={{ emptyText: <Empty description="无售后退款单" /> }} />
              </div>
            ),
          },
          {
            key: 'matched', label: <span>已匹配 <Tag color="green" style={{ marginLeft: 4 }}>{matched.length}</Tag></span>,
            children: (
              <div>
                <Space wrap style={{ marginBottom: 8 }}>
                  <Input.Search allowClear placeholder="搜订单号/商品/规格" style={{ width: 220 }} onChange={e => setSearch(e.target.value)} />
                  <Button size="small" type={onlyLoss ? 'primary' : 'default'} onClick={() => setOnlyLoss(!onlyLoss)}>只看亏损</Button>
                </Space>
                <Table rowKey="order_no" size="small" dataSource={mrows} loading={loading}
                  scroll={{ x: 860 }}
                  pagination={{ pageSize: 50, showTotal: t => `共 ${t} 单` }}
                  columns={[
                    { title: '订单号', dataIndex: 'order_no', width: 180, fixed: 'left', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
                    { title: '商品', dataIndex: 'product_name', width: 160, ellipsis: true },
                    { title: '规格', dataIndex: 'spec_code', width: 120, render: v => <span style={{ fontSize: 12, color: '#8C8178' }}>{v || '—'}</span> },
                    { title: '到账', dataIndex: 'income', width: 100, align: 'right', render: v => money(v) },
                    { title: '商品成本', dataIndex: 'product_cost', width: 90, align: 'right', render: v => '¥' + yuan(v) },
                    { title: '运费', dataIndex: 'shipping_fee', width: 80, align: 'right', render: v => '¥' + yuan(v) },
                    { title: '辅材', dataIndex: 'packaging_cost', width: 80, align: 'right', render: v => '¥' + yuan(v) },
                    { title: '利润', dataIndex: 'order_profit', width: 100, align: 'right', render: v => money(v) },
                  ]}
                  locale={{ emptyText: <Empty description="暂无匹配订单" /> }} />
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
}
